import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { invoiceService, userService } from '../firebase/services';

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getInvoiceById(invoiceId);
      setInvoice(data);
      
      // Fetch owner information if ownerId exists
      if (data.ownerId) {
        try {
          const ownerData = await userService.getUserById(data.ownerId);
          setOwnerInfo(ownerData);
        } catch (ownerErr) {
          console.error('Error loading owner info:', ownerErr);
          // Don't fail the entire load if owner info fails
        }
      }
      
      setError(null);
    } catch (err) {
      setError('Failed to load invoice details');
      console.error('Error loading invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${parseFloat(amount).toLocaleString()}`;
  };

  const getDurationUnit = (unit) => {
    const unitMap = {
      'hourly': 'Hr',
      'daily': 'Day',
      'weekly': 'Week',
      'monthly': 'Month'
    };
    return unitMap[unit] || unit;
  };

  const formatFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      // Handle Firestore Timestamp object
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000);
        return format(date, 'dd-MMM-yy');
      }
      // Handle Firestore timestamp format with __time__
      if (timestamp.__time__) {
        return format(new Date(timestamp.__time__), 'dd-MMM-yy');
      }
      // Handle regular date string
      return format(new Date(timestamp), 'dd-MMM-yy');
    } catch {
      return String(timestamp || '-');
    }
  };

  const formatPayDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let date;
      // Handle Firestore Timestamp object
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle Firestore timestamp format with __time__
      else if (timestamp.__time__) {
        date = new Date(timestamp.__time__);
      }
      // Handle regular date string
      else {
        date = new Date(timestamp);
      }
      
      // Convert to IST (UTC+5:30)
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
      return format(istDate, 'dd-MMM-yy HH:mm');
    } catch {
      return String(timestamp || '-');
    }
  };

  const transactions = invoice?.transactions || [];
  const totalPages = Math.ceil(transactions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentTransactions = transactions.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const handlePayButtonClick = async () => {
    if (!invoice.ownerId) {
      alert('Owner ID not found');
      return;
    }

    setShowPaymentModal(true);
    setPaymentLoading(true);

    try {
      const userData = await userService.getUserById(invoice.ownerId);
      setPaymentDetails(userData);
    } catch (err) {
      console.error('Error loading payment details:', err);
      alert('Failed to load payment details');
    } finally {
      setPaymentLoading(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentDetails(null);
    setShowCloseConfirmation(false);
  };

  const handleClosePaymentModal = () => {
    setShowCloseConfirmation(true);
  };

  const handlePaymentConfirmation = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmPayment = async () => {
    try {
      setUpdatingPayment(true);
      await invoiceService.updatePaymentStatus(invoiceId, 'paid');
      
      // Reload invoice to get updated data
      await loadInvoice();
      
      setShowConfirmationModal(false);
      setShowPaymentModal(false);
      setPaymentDetails(null);
      
      // Show success message
      alert('Payment status updated successfully!');
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Failed to update payment status. Please try again.');
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleCancelPayment = () => {
    setShowConfirmationModal(false);
  };

  const handleCloseConfirmationYes = async () => {
    try {
      setUpdatingPayment(true);
      await invoiceService.updatePaymentStatus(invoiceId, 'paid');
      
      // Reload invoice to get updated data
      await loadInvoice();
      
      setShowCloseConfirmation(false);
      setShowPaymentModal(false);
      setPaymentDetails(null);
      
      // Show success message
      alert('Payment status updated successfully!');
    } catch (error) {
      console.error('Error updating payment status:', error);
      alert('Failed to update payment status. Please try again.');
    } finally {
      setUpdatingPayment(false);
    }
  };

  const handleCloseConfirmationNo = () => {
    setShowCloseConfirmation(false);
    setShowPaymentModal(false);
    setPaymentDetails(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">{error || 'Invoice not found'}</div>
          <button 
            onClick={() => navigate('/invoices')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/invoices')}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">Invoice Details</h1>
            </div>
            <div className="text-sm text-gray-500">
              Invoice #{String(invoice.invoiceNumber || '')}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Invoice Header Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Invoice Number</label>
              <p className="mt-1 text-sm text-gray-900">{String(invoice.invoiceNumber || '-')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Owner Mobile</label>
              <p className="mt-1 text-sm text-gray-900">{String(invoice.ownerMobile || '-')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Statement Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatFirestoreTimestamp(invoice.statementDate)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">From Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatFirestoreTimestamp(invoice.from)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">To Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatFirestoreTimestamp(invoice.to)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Payment Status</label>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
                invoice.paymentStatus === 'Paid' 
                  ? 'bg-green-100 text-green-800'
                  : invoice.paymentStatus === 'Unpaid'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {invoice.paymentStatus || 'Unknown'}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Pay Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatPayDate(invoice.payDate)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">To Be Paid Amount</label>
              <p className="mt-1 text-lg font-bold text-gray-900">{formatCurrency(invoice.earning)}</p>
            </div>
            <div className="col-span-full">
              <button
                onClick={handlePayButtonClick}
                className="mt-4 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>

        {/* Total Earning Breakup Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">To Be Paid Amount Breakup</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Price (Forwarded to Owner)</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(transactions.reduce((sum, t) => sum + (parseFloat(t.price) || 0), 0))}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Kerblet Handling Fee</label>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(transactions.reduce((sum, t) => sum + (parseFloat(t.parkingfee) || 0), 0))}
                </p>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-md font-medium text-gray-700 mb-3">GST Breakdown (on Kerblet Handling Fee)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500">CGST</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatCurrency(transactions.reduce((sum, t) => {
                      const cgst = typeof t.cgst === 'string' ? parseFloat(t.cgst.replace('₹', '')) : parseFloat(t.cgst) || 0;
                      return sum + cgst;
                    }, 0))}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">SGST</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatCurrency(transactions.reduce((sum, t) => {
                      const sgst = typeof t.sgst === 'string' ? parseFloat(t.sgst.replace('₹', '')) : parseFloat(t.sgst) || 0;
                      return sum + sgst;
                    }, 0))}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center">
                <label className="text-lg font-semibold text-gray-900">Total Amount</label>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(transactions.reduce((sum, t) => {
                    const total = typeof t.total === 'string' ? parseFloat(t.total.replace('₹', '')) : parseFloat(t.total) || 0;
                    return sum + total;
                  }, 0))}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Total = Price + Kerblet Handling Fee + CGST + SGST
              </p>
            </div>
          </div>
        </div>

        {/* Owner Information Card */}
        {ownerInfo && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1 text-sm text-gray-900">
                  {String(ownerInfo.firstname || '')} {String(ownerInfo.lastname || '')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 1</label>
                <p className="mt-1 text-sm text-gray-900">{String(ownerInfo.address1 || '-')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 2</label>
                <p className="mt-1 text-sm text-gray-900">{String(ownerInfo.address2 || '-')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 3</label>
                <p className="mt-1 text-sm text-gray-900">{String(ownerInfo.address3 || '-')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">City</label>
                <p className="mt-1 text-sm text-gray-900">{String(ownerInfo.city || '-')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">State</label>
                <p className="mt-1 text-sm text-gray-900">{String(ownerInfo.state || '-')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Zip Code</label>
                <p className="mt-1 text-sm text-gray-900">{String(ownerInfo.zipcode || '-')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Transactions Section */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Transactions</h2>
            <p className="text-sm text-gray-500 mt-1">
              {transactions.length} transactions found
            </p>
          </div>

          {transactions.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paid Amount
                      </th>
                                             <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Vehicle Number
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Parking Name
                       </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Type
                       </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                                         {currentTransactions.map((transaction, index) => (
                       <tr key={index}>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                           {formatFirestoreTimestamp(transaction.reserveDate)}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                           {String(transaction.duration || '0')} {getDurationUnit(transaction.durationunit)}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                           {formatCurrency(transaction.price)}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                           {(transaction.type === 'Coupon' && transaction.dealID) 
                             ? 'Not Applicable'
                             : String(transaction.vehicleNumber || '-')
                           }
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                           {String(transaction.parkingName || '-')}
                         </td>
                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                           <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                             transaction.type === 'reservation' 
                               ? 'bg-blue-100 text-blue-800'
                               : (transaction.type === 'Coupon' && transaction.dealID)
                               ? 'bg-green-100 text-green-800'
                               : transaction.type === 'Coupon'
                               ? 'bg-green-100 text-green-800'
                               : 'bg-gray-100 text-gray-800'
                           }`}>
                             {transaction.type === 'reservation' 
                               ? 'Reservation'
                               : (transaction.type === 'Coupon' && transaction.dealID)
                               ? 'Deal-Voucher'
                               : String(transaction.type || '-')
                             }
                           </span>
                         </td>
                       </tr>
                     ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(endIndex, transactions.length)}</span> of{' '}
                        <span className="font-medium">{transactions.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeftIcon className="h-5 w-5" />
                        </button>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNum === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-500">No transactions found for this invoice.</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Payment Details</h2>
              <button
                onClick={handleClosePaymentModal}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {paymentLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading payment details...</p>
              </div>
            ) : paymentDetails ? (
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Side - Payment Information */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Bank Account Number</label>
                      <p className="mt-1 text-lg font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {paymentDetails.account_number || 'Not Available'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500">IFSC Code</label>
                      <p className="mt-1 text-lg font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {paymentDetails.ifsc_code || 'Not Available'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500">GSTIN</label>
                      <p className="mt-1 text-lg font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {paymentDetails.gstin || 'Not Available'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500">UPI Code</label>
                      <p className="mt-1 text-lg font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {paymentDetails.upi_code || 'Not Available'}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        Amount to Pay: <span className="font-semibold text-lg text-green-600">{formatCurrency(invoice.earning)}</span>
                      </p>
                    </div>
                  </div>

                  {/* Right Side - QR Code */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">UPI QR Code</h3>
                    
                    {paymentDetails.upi_qr_code_url ? (
                      <div className="flex flex-col items-center">
                        <div className="bg-white p-4 rounded-lg shadow-lg border">
                          <img
                            src={paymentDetails.upi_qr_code_url}
                            alt="UPI QR Code"
                            className="w-64 h-64 object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div className="hidden text-center text-gray-500">
                            <p>QR Code not available</p>
                          </div>
                        </div>
                        <p className="mt-4 text-sm text-gray-600 text-center">
                          Scan this QR code to make payment
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">QR Code not available</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      onClick={handleClosePaymentModal}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-red-600">Failed to load payment details</p>
                <button
                  onClick={handleClosePaymentModal}
                  className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Payment
              </h3>
              <p className="text-gray-600 mb-6">
                Have you manually processed the payment of <span className="font-semibold text-green-600">{formatCurrency(invoice.earning)}</span> to the owner?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleCancelPayment}
                  disabled={updatingPayment}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  No, Not Yet
                </button>
                <button
                  onClick={handleConfirmPayment}
                  disabled={updatingPayment}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                >
                  {updatingPayment && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  )}
                  Yes, Payment Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Modal */}
      {showCloseConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Close
              </h3>
              <p className="text-gray-600 mb-6">
                Have you made the payment of <span className="font-semibold text-green-600">{formatCurrency(invoice.earning)}</span> to the owner?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleCloseConfirmationNo}
                  disabled={updatingPayment}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  No, Not Yet
                </button>
                <button
                  onClick={handleCloseConfirmationYes}
                  disabled={updatingPayment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {updatingPayment && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  )}
                  Yes, I Made Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail; 